import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type {
  Currency,
  Fee,
  FeeType,
  Order,
  PaymentMethod,
} from '@hero/schema';
import type { OrderUpdateAPI, RouteUpdateAPI } from '@hero/api';
import {
  computeAmountPreview,
  getDisplayPriceCategory,
  getTaxRate,
  isInternalShipment,
} from '@hero/core';
import {
  useOrderApi,
  useRouteCreateApi,
  useRouteUpdateApi,
  useUpdateOrderPriceApi,
} from '@hero/api-hooks';
import { useApi } from '@hero/context';
import { useViewerLeg } from '@hero/web-hooks';
import {
  usePublishOrderStore,
  useOrderDraftStore,
  buildRouteCreatePayload,
  buildRouteCreatePayloadAsync,
  createRouteRegionSearchFn,
  findMatchingExistingRoute,
  useExistingRouteForLane,
  type PublishOrderPriceOverride,
} from '@hero/hooks-shared';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet/Sheet';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox/Checkbox';
import Spinner from '../../../app/Spinner/Spinner';
import { Price } from '../../../app/Price';
import { PriceInput } from '../../../app/PriceInput';
import { SelectAuto, type SelectAutoOption } from '../../../app/Select';
import {
  PlusIcon,
  TrashIcon,
  AlertCircleIcon,
  BankIcon,
  CashIcon,
} from '@hero/web-icons';
import { cn } from '@hero/web-lib';

const FEE_TYPES: FeeType[] = [
  'overnight_stay',
  'additional_location',
  'destination_waiting_too_long',
  'destination_late_arrival',
  'items_insurance',
  'other',
];

/**
 * Fees that the user shouldn't edit by hand. `items_insurance` is
 * computed from `Order.load.totalItemsCosts` by the price service and
 * gets re-applied on every price-edit save, so showing it as an
 * editable row would just confuse the user (they can't move the value
 * either way).
 */
const HIDDEN_FEE_TYPES: ReadonlySet<FeeType> = new Set(['items_insurance']);

/**
 * Payment-method affordances rendered inside the Total card. `icon`
 * is a component reference (not a JSX element) so the row can size it
 * via `className` without the caller pre-baking that decision.
 */
const PAYMENT_METHOD_OPTIONS: ReadonlyArray<{
  value: PaymentMethod;
  icon: React.FC<{ className?: string }>;
}> = [
  { value: 'bank_transfer', icon: BankIcon },
  { value: 'cash', icon: CashIcon },
];

/**
 * Saudi VAT rate sourced from `@hero/core`'s shared price config —
 * same registry the backend reads from. Used as the fallback when the
 * leg's `Amount.tax.applied` is empty (which means the leg was
 * persisted in a tax-on path before the rate was set; we still want
 * to show a sensible tax line until the next save normalises it).
 */
const DEFAULT_VAT_RATE = getTaxRate('saudi_vat');

interface FeeDraft {
  type: FeeType;
  amount: number;
}

function pricesDiffer(a: number | undefined, b: number | undefined): boolean {
  return a !== undefined && b !== undefined && Math.abs(a - b) > 0.001;
}

interface UseCustomPriceToggleProps {
  useCustomPrice: boolean;
  onUseCustomPriceChange: (next: boolean) => void;
  agreedSubtotal: number | undefined;
  loadPrice: number | undefined;
  currency: Currency;
}

/**
 * Shared "Use custom price" affordance — publish draft + order table
 * edit both surface the same toggle when a saved route rate exists.
 */
function UseCustomPriceToggle({
  useCustomPrice,
  onUseCustomPriceChange,
  agreedSubtotal,
  loadPrice,
  currency,
}: UseCustomPriceToggleProps) {
  const { t } = useTranslation(['m-order']);
  const priceDiffersFromAgreed = pricesDiffer(agreedSubtotal, loadPrice);

  return (
    <RoutePromptRow
      checked={useCustomPrice}
      onCheckedChange={onUseCustomPriceChange}
      label={
        <span className="flex flex-wrap items-center gap-1">
          {useCustomPrice
            ? t('drawer.price_edit.use_custom_price_checked', {
                defaultValue: 'Use custom price for this destination',
              })
            : t('drawer.price_edit.use_custom_price_unchecked', {
                defaultValue: 'Use custom price instead of the calculated rate',
              })}
          {!useCustomPrice && agreedSubtotal !== undefined ? (
            <>
              <span>·</span>
              <Price
                amount={agreedSubtotal}
                currency={currency}
                size="xs"
                className="font-semibold"
              />
            </>
          ) : null}
          {useCustomPrice && priceDiffersFromAgreed && agreedSubtotal !== undefined ? (
            <>
              <span className="text-blue-700/70">
                {t('drawer.price_edit.was_agreed_rate', {
                  defaultValue: '(was',
                })}
              </span>
              <Price
                amount={agreedSubtotal}
                currency={currency}
                size="xs"
                className="font-semibold text-blue-700/70"
              />
              <span className="text-blue-700/70">)</span>
            </>
          ) : null}
        </span>
      }
    />
  );
}

/**
 * Two-mode drawer for editing a per-leg price.
 *
 *   - **`mode: 'order'`** — the canonical path used everywhere a real
 *     `Order` exists (order drawer's price tab, order table price
 *     cell). Saves through `useUpdateOrderPriceApi` and exposes the
 *     "save / update Route" prompts.
 *   - **`mode: 'publish-draft'`** — the publish-page surface (per-
 *     destination edit on `RouteSection`). There is no Order yet, so
 *     saving writes to the publish store via `setPriceOverride`; the
 *     publish payload forwards the override as `routes[i].price` (see
 *     `buildCreateMultiOrderPayload`). Route prompts and the leg-only
 *     banners are hidden — they have no meaning before persistence.
 *   - **`mode: 'order-draft'`** — any surface that scopes its own
 *     `OrderDraftProvider` (agent confirmation card, reorder modal,
 *     edit drawer). Reads & writes the scoped draft store via
 *     `useOrderDraftStore`. Same form as `publish-draft` minus the
 *     singleton coupling.
 *
 * Default `mode: 'order'` is retained when the prop is omitted so
 * existing callers (`OrderActions`, `PriceCell`) keep working without
 * a code change.
 */
export type OrderPriceEditDrawerProps =
  | OrderPriceEditDrawerOrderProps
  | OrderPriceEditDrawerPublishDraftProps
  | OrderPriceEditDrawerOrderDraftProps;

interface OrderPriceEditDrawerCommonProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface OrderPriceEditDrawerOrderProps extends OrderPriceEditDrawerCommonProps {
  mode?: 'order';
  order: Order | null;
  onSaved?: (order: Order) => void;
  /**
   * Defensive read-only gate. The actions menu hides the trigger
   * when `useOrderActions(order).canEditPrice` is `false`, but the
   * drawer accepts the same flag so it stays locked even if it's
   * opened via a different path (e.g. programmatic open, deep
   * link). Defaults to `true` (editable) for backward compat.
   */
  canEditPrice?: boolean;
}

export interface OrderPriceEditDrawerPublishDraftProps extends OrderPriceEditDrawerCommonProps {
  mode: 'publish-draft';
  /** 0-based destination index in the publish store. */
  destinationIndex: number;
}

export interface OrderPriceEditDrawerOrderDraftProps extends OrderPriceEditDrawerCommonProps {
  mode: 'order-draft';
  /** 0-based destination index in the scoped order-draft store. */
  destinationIndex: number;
}

/**
 * Strip the auto-managed fees (currently just `items_insurance`) so
 * the user only sees rows they can actually move. The price service
 * re-applies the auto fees on every save, so omitting them from the
 * draft list is safe.
 */
function asEditableFees(applied: Fee[] | undefined): FeeDraft[] {
  return (applied ?? [])
    .filter(f => !HIDDEN_FEE_TYPES.has(f.type))
    .map(f => ({ type: f.type, amount: f.amount }));
}

/**
 * Phase 7+ — order price edit drawer.
 *
 * Surfaces:
 *  - load price (renamed from "subtotal", clearly flagged "before tax");
 *  - fees as two-line cards with translated labels via `<SelectAuto/>`;
 *  - payment method (`bank_transfer` / `cash`) — defaults to bank
 *    transfer per network convention, persisted on every contract leg;
 *  - tax-inclusive total — no more "estimated" hedging, the math is
 *    deterministic from what the user typed;
 *  - custom-price banner when the contract was NOT priced from a
 *    saved Route, so the user immediately understands they're working
 *    off a one-off price;
 *  - inline "save as Route" / "update Route" prompts that let the
 *    user promote a per-order tweak into the saved-rate book between
 *    the two parties without leaving the drawer.
 *
 * Reads and writes the **viewer's leg** via `useViewerLeg(order)` —
 * the leg where the current user is a party (buyer OR seller). That
 * matches the backend's leg-membership authorization, so the price
 * the user sees here is the price the backend will let them change.
 *
 * Dispatches between order-mode (the canonical path) and the
 * publish-draft variant used by the new-order page; see
 * {@link OrderPriceEditDrawerProps} for the shape.
 */
export function OrderPriceEditDrawer(props: OrderPriceEditDrawerProps) {
  if (props.mode === 'publish-draft') {
    return <OrderPriceEditDrawerPublishDraft {...props} />;
  }
  if (props.mode === 'order-draft') {
    return <OrderPriceEditDrawerOrderDraft {...props} />;
  }
  return <OrderPriceEditDrawerOrderMode {...props} />;
}

function OrderPriceEditDrawerOrderMode({
  order,
  isOpen,
  onClose,
  onSaved,
  canEditPrice = true,
}: OrderPriceEditDrawerOrderProps) {
  const { t } = useTranslation(['m-order', 'c-main', 'c-price']);
  const api = useApi();

  // List / drawer shells sometimes pass a partial Order (no `contracts[]`).
  // Re-fetch the canonical doc when the drawer opens so leg resolution
  // doesn't stall forever on stale rows.
  const {
    data: freshOrder,
    isLoading: isLoadingFreshOrder,
    isFetched: isFreshOrderFetched,
  } = useOrderApi(order?._id, { enabled: isOpen && !!order?._id });

  const effectiveOrder = freshOrder ?? order;
  const leg = useViewerLeg(effectiveOrder);
  const currency: Currency = leg?.amount?.currency ?? 'SAR';
  const taxRate = leg?.amount?.tax?.applied?.[0]?.percentage ?? DEFAULT_VAT_RATE;
  const isTaxable = (leg?.amount?.tax?.applied?.length ?? 0) > 0;

  const sellerName = leg?.seller?.name ?? '';
  const buyerName = leg?.buyer?.name ?? '';

  // Open-time lookup: does a Route already exist for this lane,
  // regardless of whether `leg.route` was set at create time? Drives
  // the "save vs update" prompt below so re-opening the drawer on an
  // older one-time order whose lane has since had a Route saved
  // doesn't mis-offer "Save as route" (which would fail with
  // `route_vehicle_overlap` on save).
  const {
    effectiveRouteRef,
    isLoading: isLoadingExistingRoute,
  } = useExistingRouteForLane({ order: effectiveOrder, leg, enabled: isOpen });

  const [loadPrice, setLoadPrice] = useState<number | undefined>(undefined);
  const [fees, setFees] = useState<FeeDraft[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('bank_transfer');
  const [saveAsRoute, setSaveAsRoute] = useState(false);
  const [updateRoute, setUpdateRoute] = useState(false);
  const [useCustomPrice, setUseCustomPrice] = useState(false);
  const [hasSeeded, setHasSeeded] = useState(false);

  // Seed inputs each time the drawer opens for a new order. Edits the
  // user makes in-flight should NOT be clobbered by a stale re-seed —
  // keying on `order._id` means the drawer treats each open as a fresh
  // edit.
  useEffect(() => {
    if (!isOpen || !leg) {
      if (!isOpen) setHasSeeded(false);
      return;
    }
    const legPrice = leg.amount.itemsOriginalSubtotal;
    const agreed = leg.route?.price;
    const category = getDisplayPriceCategory(leg);
    const hasAgreed =
      (category === 'agreement' || category === 'everyone') &&
      agreed !== undefined &&
      agreed > 0;
    const initiallyCustom =
      category === 'one_time' || (hasAgreed && pricesDiffer(legPrice, agreed));
    setLoadPrice(initiallyCustom ? legPrice : hasAgreed ? agreed : legPrice);
    setFees(asEditableFees(leg.amount.fees?.applied));
    setPaymentMethod(leg.payment?.method ?? 'bank_transfer');
    setUseCustomPrice(initiallyCustom);
    setSaveAsRoute(false);
    setUpdateRoute(false);
    setHasSeeded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, effectiveOrder?._id, leg?.contractId]);

  // Re-derive the live preview through the shared `computeAmountPreview`
  // — same math as the backend `AmountService`, so what the user sees
  // here matches exactly what the server will store after Save. Adds
  // the leg's existing tax stack (with zeroed amounts; the previewer
  // re-computes against the new fee-inclusive subtotal) when the leg
  // is taxable, otherwise leaves tax empty.
  // See `libs/shared/core/src/amount/README.md`.
  const preview = useMemo(() => {
    return computeAmountPreview({
      currency,
      itemsOriginalSubtotal: loadPrice ?? 0,
      fees: {
        applied: fees.map(f => ({
          type: f.type,
          amount: f.amount || 0,
          calcType: 'add_to_subtotal' as const,
        })) as Fee[],
      },
      tax: isTaxable
        ? {
            applied: (leg?.amount.tax.applied ?? []).map(t => ({ ...t, amount: 0 })),
          }
        : undefined,
    });
  }, [currency, loadPrice, fees, isTaxable, leg]);
  // `subtotal` here is "load price + fees" (post-discount, pre-tax).
  // Aliased to `base` for the existing JSX that calls it that.
  const base = preview.subtotal;
  const tax = preview.tax.total;
  const total = preview.total;

  // Use the shared display selector so the drawer agrees with reorder
  // and badges on final commercial state (priceBasis), not just create-
  // time strategy. See `getDisplayPriceCategory` in `@hero/core`.
  const priceCategory = getDisplayPriceCategory(leg);
  const isAgreementOrEveryone = priceCategory === 'agreement' || priceCategory === 'everyone';
  const isOneTime = priceCategory === 'one_time';
  const isUnresolved = priceCategory === 'unresolved';

  // Defensive read-only lock — same idea as the mobile sheet. The
  // actions menu hides the trigger when `canEditPrice` is `false`;
  // this gate makes the drawer a no-op fallback if it's opened by
  // some other path. When locked we suppress the category banners
  // and route prompts in favour of a single "this order can no
  // longer be edited" notice.
  const isReadOnly = !canEditPrice;

  // `effectiveRouteRef.price` is the Route's current price (or the
  // frozen leg snapshot when `leg.route` is set). Source of truth for
  // the "saved rate" prompts so the number quoted in the UI is always
  // what the route actually quotes today, not what it quoted when this
  // order was originally booked.
  const savedRoutePrice = effectiveRouteRef?.price;
  const hasAgreedPrice =
    isAgreementOrEveryone &&
    savedRoutePrice !== undefined &&
    savedRoutePrice > 0;
  const agreedSubtotal = hasAgreedPrice ? savedRoutePrice : undefined;
  const priceDiffersFromRoute =
    savedRoutePrice !== undefined &&
    loadPrice !== undefined &&
    Math.abs(savedRoutePrice - loadPrice) > 0.001;

  const handleLoadPriceChange = (next: number | undefined) => {
    setLoadPrice(next);
    if (hasAgreedPrice && agreedSubtotal !== undefined && pricesDiffer(agreedSubtotal, next)) {
      setUseCustomPrice(true);
    }
  };

  const handleUseCustomPriceChange = (next: boolean) => {
    setUseCustomPrice(next);
    if (!next && agreedSubtotal !== undefined) {
      setLoadPrice(agreedSubtotal);
      setFees([]);
      setUpdateRoute(false);
    }
  };

  const handleResetToAgreedPrice = () => {
    if (agreedSubtotal === undefined) return;
    setUseCustomPrice(false);
    setLoadPrice(agreedSubtotal);
    setFees([]);
    setUpdateRoute(false);
    setSaveAsRoute(false);
  };

  // "Save as Route" is only meaningful when NO Route exists for this
  // lane yet. We branch on `effectiveRouteRef` (leg snapshot ∪ live
  // lookup) so the prompt disappears the moment we know the parties
  // already have one — even if the leg itself was priced one-off.
  const showSaveRoutePrompt =
    !effectiveRouteRef &&
    (isOneTime || isUnresolved) &&
    (loadPrice ?? 0) > 0 &&
    !!sellerName &&
    !!buyerName;
  // "Update Route" — fires whenever a route exists for the lane AND
  // the load price the user is committing differs from what the route
  // currently quotes. Works for both:
  //   1. legs originally priced from a Route (the canonical case);
  //   2. one-time / unresolved legs whose lane has since had a Route
  //      saved — the open-time probe surfaces that ref so the user
  //      can opt-in to update it instead of being silently routed
  //      back to "Save as Route" (which would fail with
  //      `route_vehicle_overlap`).
  const showUpdateRoutePrompt =
    !!effectiveRouteRef &&
    priceDiffersFromRoute &&
    (loadPrice ?? 0) > 0 &&
    !!sellerName &&
    !!buyerName;

  // Reset the route toggles when the underlying eligibility changes
  // (e.g. user clears the load price → save-as-route prompt vanishes →
  // its checkbox state shouldn't leak into the next save).
  useEffect(() => {
    if (!showSaveRoutePrompt) setSaveAsRoute(false);
  }, [showSaveRoutePrompt]);
  useEffect(() => {
    if (!showUpdateRoutePrompt) setUpdateRoute(false);
  }, [showUpdateRoutePrompt]);

  const { mutate: createRoute, isPending: isCreatingRoute } = useRouteCreateApi({
    onSuccess: () => {
      toast.success(
        t('drawer.price_edit.route_saved_toast', { defaultValue: 'Route price saved' }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const { mutate: updateRouteApi, isPending: isUpdatingRoute } = useRouteUpdateApi({
    onSuccess: () => {
      toast.success(
        t('drawer.price_edit.route_updated_toast', { defaultValue: 'Route price updated' }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const { mutate: updateOrder, isPending: isSavingOrder } = useUpdateOrderPriceApi({
    onSuccess: result => {
      const saved = result as Order;
      toast.success(t('drawer.price_edit.saved_toast', { defaultValue: 'Price saved' }));
      // After the order PATCH lands, fire whichever Route side-effect
      // the user opted into. We do these post-save (not in parallel)
      // so a Route failure can't roll back the price change the user
      // already committed to.
      if (updateRoute && effectiveRouteRef && loadPrice !== undefined) {
        const payload: RouteUpdateAPI['req'] = {
          _id: effectiveRouteRef.routeId,
          price: loadPrice,
        };
        updateRouteApi(payload);
      } else if (saveAsRoute && effectiveOrder && leg && loadPrice !== undefined) {
        void (async () => {
          try {
            // Narrow the SDK to the small shape the route helpers need.
            // The SDK's `region.search` requires `limit: number` (zod
            // applies the default at parse time) and `getAllCities`
            // expects an explicit `undefined` arg under strict typing.
            const sdkAdapter = {
              common: {
                region: {
                  search: (req: { q: string; limit?: number }) =>
                    api.common.region.search({ q: req.q, limit: req.limit ?? 5 }),
                  getAllCities: () => api.common.region.getAllCities(undefined),
                },
              },
              routes: { getAll: api.routes.getAll },
            };
            const searchRegion = createRouteRegionSearchFn(sdkAdapter);
            const createPayload =
              buildRouteCreatePayload({ order: effectiveOrder, leg, price: loadPrice, currency }) ??
              (await buildRouteCreatePayloadAsync(
                { order: effectiveOrder, leg, price: loadPrice, currency },
                searchRegion,
              ));
            if (!createPayload) {
              toast.error(
                t('drawer.price_edit.route_save_failed_toast', {
                  defaultValue:
                    'Price saved, but we could not save a route for this lane. Check vehicle type and destinations.',
                }),
              );
              return;
            }
            const existing = await findMatchingExistingRoute(sdkAdapter, createPayload);
            if (existing) {
              updateRouteApi({ _id: existing._id, price: loadPrice });
            } else {
              createRoute(createPayload);
            }
          } catch {
            toast.error(
              t('drawer.price_edit.route_save_failed_toast', {
                defaultValue:
                  'Price saved, but we could not save a route for this lane. Check vehicle type and destinations.',
              }),
            );
          }
        })();
      }
      onSaved?.(saved);
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const isSaving = isSavingOrder || isCreatingRoute || isUpdatingRoute;

  const handleSave = () => {
    if (!effectiveOrder || !leg || loadPrice === undefined) return;
    const effectiveLoadPrice =
      hasAgreedPrice && !useCustomPrice && agreedSubtotal !== undefined
        ? agreedSubtotal
        : loadPrice;
    const payload: OrderUpdateAPI['req'] = {
      _id: effectiveOrder._id,
      // Network-model auth: tell the backend which leg the user is
      // editing. The backend authorizes by leg membership (any party
      // of the leg may edit its bilateral price), not by user role.
      contractId: leg.contractId,
      beneficiaryAmount: {
        itemsOriginalSubtotal: effectiveLoadPrice,
        taxable: isTaxable,
        fees: fees.map(f => ({ type: f.type, amount: f.amount })) as Fee[],
      },
      paymentMethod,
    };
    updateOrder(payload);
  };

  const updateFee = (index: number, patch: Partial<FeeDraft>) => {
    setFees(prev => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  const removeFee = (index: number) => {
    setFees(prev => prev.filter((_, i) => i !== index));
  };

  const addFee = () => {
    // Pick the first fee type the user hasn't added yet (skipping the
    // auto-managed ones), falling back to `other` so the user can
    // stack multiple "other" rows.
    const usedTypes = new Set(fees.map(f => f.type));
    const next =
      FEE_TYPES.find(ft => !usedTypes.has(ft) && !HIDDEN_FEE_TYPES.has(ft)) ?? 'other';
    setFees(prev => [...prev, { type: next, amount: 0 }]);
  };

  const feeOptions: SelectAutoOption[] = FEE_TYPES.filter(ft => !HIDDEN_FEE_TYPES.has(ft)).map(
    ft => ({
      value: ft,
      label: t(`c-price:fee_types.${ft}`, { defaultValue: ft }),
    }),
  );

  // Ready-to-render gate. Mirrors `useOrderPriceEditManager.isReady`
  // on mobile so both surfaces show a spinner while the leg resolves
  // + inputs seed + the open-time lane probe settles. Without this
  // the sheet's slide-in animation finishes faster than the seed,
  // and the user sees an empty form flash before the actual values
  // land.
  const isResolvingOrder = isOpen && !!order?._id && isLoadingFreshOrder;
  const contractCount = effectiveOrder?.contracts?.length ?? 0;
  const isLegUnavailable =
    isOpen &&
    isFreshOrderFetched &&
    !isResolvingOrder &&
    !isLoadingExistingRoute &&
    !leg;
  const isReady =
    !!leg && hasSeeded && !isLoadingExistingRoute && !isResolvingOrder;
  const isInternal = effectiveOrder ? isInternalShipment(effectiveOrder) : false;

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 bg-white w-[520px] sm:max-w-[520px]">
        <SheetHeader className="px-5 pt-5 pb-3 border-b">
          <SheetTitle>
            {t('drawer.price_edit.title', { defaultValue: 'Edit price' })}
          </SheetTitle>
          <SheetDescription>
            {t('drawer.price_edit.description', {
              defaultValue: 'Update the load price, fees, and payment method.',
            })}
          </SheetDescription>
        </SheetHeader>

        {isLegUnavailable ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-5 min-h-[240px] text-center">
            <AlertCircleIcon className="size-8 text-amber-600 shrink-0" />
            <div className="space-y-1.5 max-w-sm">
              <p className="text-sm text-gray-700">
                {isInternal
                  ? t('internal_shipment.price_edit_notice')
                  : contractCount === 0
                    ? t('contracts.no_contracts')
                    : t('drawer.price_edit.no_viewer_leg', {
                        defaultValue:
                          'We could not find a price contract for your account on this order.',
                      })}
              </p>
            </div>
            <Button variant="outline" onClick={onClose}>
              {t('c-main:close', { defaultValue: 'Close' })}
            </Button>
          </div>
        ) : !isReady ? (
          <div className="flex-1 flex items-center justify-center min-h-[240px]">
            <Spinner className="size-6 text-primary" />
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {isReadOnly && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50/70 px-3 py-2.5">
              <AlertCircleIcon className="size-4 text-rose-600 mt-0.5 shrink-0" />
              <div className="text-[12px] font-medium text-rose-900">
                {t('drawer.price_edit.read_only_notice', {
                  defaultValue: 'This order can no longer be edited.',
                })}
              </div>
            </div>
          )}
          {/* Price-category awareness banner. Each category gets its
              own copy so the user knows whether they're working off a
              one-off number, an unresolved (zero) leg awaiting input,
              or already-saved Route price. Categories come from the
              shared `getPriceCategory` selector — same source of
              truth as the reorder modal badge and the publish-flow
              `PriceSourceCard`. */}
          {!isReadOnly && isOneTime && sellerName && buyerName && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2.5">
              <AlertCircleIcon className="size-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <div className="text-[12px] font-medium text-amber-900">
                  {t('drawer.price_edit.custom_price_badge', { defaultValue: 'Custom price' })}
                </div>
                <div className="text-[11px] text-amber-800">
                  {t('drawer.price_edit.custom_price_hint', {
                    defaultValue:
                      'This price is set per-order, not from a saved route between {{seller}} and {{buyer}}.',
                    seller: sellerName,
                    buyer: buyerName,
                  })}
                </div>
              </div>
            </div>
          )}
          {!isReadOnly && isUnresolved && sellerName && buyerName && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50/70 px-3 py-2.5">
              <AlertCircleIcon className="size-4 text-rose-600 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <div className="text-[12px] font-medium text-rose-900">
                  {t('drawer.price_edit.unresolved_price_badge', {
                    defaultValue: 'Awaiting price',
                  })}
                </div>
                <div className="text-[11px] text-rose-800">
                  {t('drawer.price_edit.unresolved_price_hint', {
                    defaultValue:
                      'No saved route between {{seller}} and {{buyer}} matched this lane. Enter a price to confirm the order.',
                    seller: sellerName,
                    buyer: buyerName,
                  })}
                </div>
              </div>
            </div>
          )}
          {!isReadOnly && priceCategory === 'agreement' && sellerName && buyerName && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2.5">
              <AlertCircleIcon className="size-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <div className="text-[12px] font-medium text-emerald-900">
                  {t('drawer.price_edit.agreement_price_badge', {
                    defaultValue: 'Agreement price',
                  })}
                </div>
                <div className="text-[11px] text-emerald-800">
                  {t('drawer.price_edit.draft_agreement_price_hint', {
                    defaultValue:
                      'A saved rate applies to this lane. Uncheck custom price below to keep it.',
                  })}
                </div>
              </div>
            </div>
          )}
          {!isReadOnly && priceCategory === 'everyone' && sellerName && buyerName && (
            <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50/70 px-3 py-2.5">
              <AlertCircleIcon className="size-4 text-sky-600 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <div className="text-[12px] font-medium text-sky-900">
                  {t('drawer.price_edit.everyone_price_badge', {
                    defaultValue: 'Public rate',
                  })}
                </div>
                <div className="text-[11px] text-sky-800">
                  {t('drawer.price_edit.draft_everyone_price_hint', {
                    defaultValue:
                      'A published rate applies to this lane. Uncheck custom price below to keep it.',
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Load price — the headline input. Replaces "subtotal" so the
              vocabulary on the screen matches the rest of the app
              (`<Price/>` already says "Load price" in c-price.json). */}
          <div className="space-y-2">
            <PriceInput
              label={t('drawer.price_edit.load_price_label', { defaultValue: 'Load price' })}
              value={loadPrice}
              onChange={handleLoadPriceChange}
              currency={currency}
              priceType="before_tax"
              hint={t('drawer.price_edit.before_tax_hint', { defaultValue: 'Before tax' })}
              disabled={isReadOnly || (hasAgreedPrice && !useCustomPrice)}
            />

            {!isReadOnly && hasAgreedPrice && (
              <UseCustomPriceToggle
                useCustomPrice={useCustomPrice}
                onUseCustomPriceChange={handleUseCustomPriceChange}
                agreedSubtotal={agreedSubtotal}
                loadPrice={loadPrice}
                currency={currency}
              />
            )}

            {/* Save-as-Route prompt — only when the order has no Route
                ref and the user has typed a positive price. Action
                fires on Save, not now. */}
            {!isReadOnly && showSaveRoutePrompt && (
              <RoutePromptRow
                checked={saveAsRoute}
                onCheckedChange={setSaveAsRoute}
                label={t('drawer.price_edit.save_route_prompt', {
                  defaultValue:
                    'Save this as the agreed price between {{seller}} and {{buyer}} for this lane?',
                  seller: sellerName,
                  buyer: buyerName,
                })}
              />
            )}

            {/* Update-Route prompt — only when this leg WAS priced from
                a Route and the user has changed the load price. */}
            {!isReadOnly && showUpdateRoutePrompt && savedRoutePrice !== undefined && (
              <RoutePromptRow
                checked={updateRoute}
                onCheckedChange={setUpdateRoute}
                label={
                  <span className="flex flex-wrap items-center gap-1">
                    {t('drawer.price_edit.update_route_prompt', {
                      defaultValue:
                        'Update the saved price between {{seller}} and {{buyer}} for this lane (was {{previous}})?',
                      seller: sellerName,
                      buyer: buyerName,
                      previous: '__previous__',
                    })
                      .split('__previous__')
                      .flatMap((chunk, i) =>
                        i === 0
                          ? [chunk]
                          : [
                              <Price
                                key="previous"
                                amount={savedRoutePrice}
                                currency={currency}
                                size="xs"
                                className="font-semibold"
                              />,
                              chunk,
                            ],
                      )}
                  </span>
                }
              />
            )}
          </div>

          {/* Fees — two-line cards. Top line: translated SelectAuto for
              the fee type. Bottom line: PriceInput + remove icon.
              Hidden entirely when the order is read-only. */}
          {!isReadOnly && (
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[12px] font-medium text-gray-700">
                  {t('drawer.price_edit.fees_label', { defaultValue: 'Fees' })}
                </label>
                <button
                  type="button"
                  onClick={addFee}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 disabled:opacity-40 disabled:pointer-events-none">
                  <PlusIcon className="size-3.5" />
                  {t('drawer.price_edit.add_fee', { defaultValue: 'Add fee' })}
                </button>
              </div>

              {fees.length === 0 ? (
                <div className="mt-2 rounded-md border border-dashed border-gray-200 p-3 text-xs text-gray-500">
                  {t('drawer.price_edit.no_fees', { defaultValue: 'No fees yet.' })}
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  {fees.map((fee, index) => (
                    <div
                      key={`${fee.type}-${index}`}
                      className="rounded-md border border-gray-200 bg-white p-2.5 space-y-2">
                      <SelectAuto
                        options={feeOptions}
                        value={fee.type}
                        onChange={value => updateFee(index, { type: value as FeeType })}
                        placeholder={t('drawer.price_edit.fees_label', { defaultValue: 'Fees' })}
                      />
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <PriceInput
                            value={fee.amount}
                            onChange={next => updateFee(index, { amount: next ?? 0 })}
                            currency={currency}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFee(index)}
                          aria-label={t('drawer.price_edit.fee_remove', {
                            defaultValue: 'Remove fee',
                          })}
                          className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                          <TrashIcon className="size-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Total — tax-inclusive. Right column shows the headline
              total via `<Price/>`; the helper line breaks it into
              pre-tax + tax so the user can audit the math. The
              payment-method selector lives in this card too because
              it answers the same question the total does ("how much
              and how do you pay it") — keeping them together avoids
              an extra section separator and reads as one decision. */}
          <div className="rounded-md border border-gray-200 bg-gray-50 overflow-hidden">
            <div className="px-3.5 py-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  {t('drawer.price_edit.total', { defaultValue: 'Total' })}
                </span>
                <Price amount={total} currency={currency} size="lg" className="font-semibold" />
              </div>
              {isTaxable && (
                <div className="flex items-center justify-between text-[11px] text-gray-500">
                  <span>
                    {t('drawer.price_edit.total_with_tax_hint', { defaultValue: 'Includes tax' })}
                    <span className="mx-1">·</span>
                    {t('drawer.price_edit.tax_line', {
                      defaultValue: 'Tax ({{percent}}%)',
                      percent: Math.round(taxRate * 100),
                    })}
                  </span>
                  <Price amount={tax} currency={currency} size="xs" />
                </div>
              )}
              {!isTaxable && (
                <div className="flex items-center justify-between text-[11px] text-gray-500">
                  <span>{t('c-price:subtotal', { defaultValue: 'Subtotal' })}</span>
                  <Price amount={base} currency={currency} size="xs" />
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 bg-white px-3.5 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[12px] font-medium text-gray-700">
                  {t('drawer.price_edit.payment_method_label', {
                    defaultValue: 'Payment method',
                  })}
                </span>
                <div className="flex items-center gap-1 rounded-md bg-gray-100 p-0.5">
                  {PAYMENT_METHOD_OPTIONS.map(({ value, icon: Icon }) => {
                    const isActive = paymentMethod === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setPaymentMethod(value)}
                        aria-pressed={isActive}
                        disabled={isReadOnly}
                        className={cn(
                          // Tinted-pill segmented control. Active pill
                          // lifts to white, gains a primary ring + a
                          // primary-colored icon/label so the picked
                          // option reads at a glance even when the
                          // user isn't focused on the control. The
                          // primary tint is the same accent used for
                          // active filters elsewhere in the dashboard.
                          'flex items-center gap-1.5 rounded px-2.5 py-1 text-[12px] font-medium transition-colors',
                          isActive
                            ? 'bg-white text-primary ring-1 ring-primary/40 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700',
                        )}>
                        <Icon
                          className={cn(
                            'size-3.5',
                            isActive ? 'text-primary' : 'text-gray-500',
                          )}
                        />
                        {t(`drawer.price_edit.payment_method.${value}`, { defaultValue: value })}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="border-t px-5 py-4 flex gap-3">
          {!isReadOnly && hasAgreedPrice && useCustomPrice ? (
            <Button variant="ghost" onClick={handleResetToAgreedPrice} disabled={isSaving || !isReady}>
              {t('drawer.price_edit.draft_clear_cta', { defaultValue: 'Reset' })}
            </Button>
          ) : (
            <span className="flex-1" />
          )}
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isSaving}>
            {isReadOnly
              ? t('c-main:crud_buttons.close', { defaultValue: 'Close' })
              : t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
          </Button>
          {!isReadOnly && (
            <Button
              className="flex-1"
              onClick={handleSave}
              disabled={loadPrice === undefined || isSaving || !isReady || !effectiveOrder || !leg}>
              {isSaving ? (
                <>
                  <Spinner className="w-4 h-4 me-2" />
                  {t('c-main:async_operations.saving', { defaultValue: 'Saving…' })}
                </>
              ) : (
                t('drawer.price_edit.save_cta', { defaultValue: 'Save price' })
              )}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Publish-draft variant — used on the per-destination price chip on
 * `RouteSection`. Same form vocabulary as the order-mode drawer
 * (load price, fees, payment method, tax-aware total) but writes to
 * the publish store instead of an existing order. The backend
 * forwards the resulting override as `routes[i].price` on
 * `CreateMultiOrderAPI`, which records `pricingStrategy:
 * 'one_time_pricing'` for that leg specifically.
 *
 * Hidden compared to order-mode:
 *   - leg-only price-category banners (agreement / public rate /
 *     unresolved) — there is no leg yet;
 *   - "save as Route" / "update Route" prompts — those require a
 *     persisted order with the right Party context.
 *
 * Currency / tax defaults come from the resolver preview when one
 * has landed for this destination, falling back to `SAR` + a
 * configured default tax rate so the user always sees a usable form.
 */
/**
 * Publish-page variant — reads the singleton `usePublishOrderStore`.
 * Thin wrapper that pulls per-destination data out of the singleton
 * and forwards to {@link OrderPriceEditDrawerDraftForm}.
 */
function OrderPriceEditDrawerPublishDraft(props: OrderPriceEditDrawerPublishDraftProps) {
  const override = usePublishOrderStore(s => s.priceOverrides[props.destinationIndex]);
  const setPriceOverride = usePublishOrderStore(s => s.setPriceOverride);
  const pricePreview = usePublishOrderStore(s => s.pricePreview);
  return (
    <OrderPriceEditDrawerDraftForm
      destinationIndex={props.destinationIndex}
      isOpen={props.isOpen}
      onClose={props.onClose}
      override={override}
      setPriceOverride={setPriceOverride}
      pricePreview={pricePreview}
    />
  );
}

/**
 * Scoped variant — reads the `useOrderDraftStore` exposed by the
 * caller's `<OrderDraftProvider>` (agent confirmation card, reorder
 * modal, edit drawer). Throws if used outside a provider so misuse
 * fails loud.
 */
function OrderPriceEditDrawerOrderDraft(props: OrderPriceEditDrawerOrderDraftProps) {
  const override = useOrderDraftStore(s => s.priceOverrides[props.destinationIndex]);
  const setPriceOverride = useOrderDraftStore(s => s.setPriceOverride);
  const pricePreview = useOrderDraftStore(s => s.pricePreview);
  return (
    <OrderPriceEditDrawerDraftForm
      destinationIndex={props.destinationIndex}
      isOpen={props.isOpen}
      onClose={props.onClose}
      override={override}
      setPriceOverride={setPriceOverride}
      pricePreview={pricePreview}
    />
  );
}

interface OrderPriceEditDrawerDraftFormProps {
  destinationIndex: number;
  isOpen: boolean;
  onClose: () => void;
  override: PublishOrderPriceOverride | undefined;
  setPriceOverride: (index: number, next: PublishOrderPriceOverride | undefined) => void;
  pricePreview:
    | {
        currency?: Currency;
        routes?: Array<{
          amount?: { subtotal?: number; currency?: Currency };
          pricingSource?: string;
        }>;
      }
    | null
    | undefined;
}

function OrderPriceEditDrawerDraftForm({
  destinationIndex,
  isOpen,
  onClose,
  override,
  setPriceOverride,
  pricePreview,
}: OrderPriceEditDrawerDraftFormProps) {
  const { t } = useTranslation(['m-order', 'c-main', 'c-price']);

  const resolverRoute = pricePreview?.routes?.[destinationIndex];
  const resolverCurrency: Currency = (resolverRoute?.amount?.currency ?? 'SAR') as Currency;
  const fallbackCurrency: Currency =
    (pricePreview?.currency as Currency | undefined) ?? resolverCurrency ?? 'SAR';
  const agreedSubtotal = resolverRoute?.amount?.subtotal;
  const pricingSource = resolverRoute?.pricingSource;
  const hasAgreedPrice =
    agreedSubtotal !== undefined &&
    agreedSubtotal > 0 &&
    !!pricingSource &&
    pricingSource !== 'unresolved' &&
    pricingSource !== 'manual' &&
    pricingSource !== 'one_time';

  const [loadPrice, setLoadPrice] = useState<number | undefined>(undefined);
  const [fees, setFees] = useState<FeeDraft[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('bank_transfer');
  const [currency, setCurrency] = useState<Currency>(fallbackCurrency);
  const [useCustomPrice, setUseCustomPrice] = useState(false);

  // Re-seed inputs each time the drawer opens for a new destination.
  // Edits the user makes in-flight should NOT be clobbered by a
  // stale re-seed — keying on `destinationIndex` means the drawer
  // treats each open as a fresh edit.
  useEffect(() => {
    if (!isOpen) return;
    setLoadPrice(
      override?.itemsOriginalSubtotal ??
        resolverRoute?.amount?.subtotal ??
        undefined,
    );
    setFees((override?.fees ?? []).map(f => ({ type: f.type, amount: f.amount })));
    setPaymentMethod('bank_transfer');
    setCurrency(override?.currency ?? fallbackCurrency);
    setUseCustomPrice(!!override);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, destinationIndex]);

  const handleLoadPriceChange = (next: number | undefined) => {
    setLoadPrice(next);
    if (
      hasAgreedPrice &&
      agreedSubtotal !== undefined &&
      next !== undefined &&
      Math.abs(agreedSubtotal - next) > 0.001
    ) {
      setUseCustomPrice(true);
    }
  };

  const handleUseCustomPriceChange = (next: boolean) => {
    setUseCustomPrice(next);
    if (!next && hasAgreedPrice && agreedSubtotal !== undefined) {
      setLoadPrice(agreedSubtotal);
      setFees([]);
    }
  };

  const taxRate = DEFAULT_VAT_RATE;

  // VAT is always applied — the network operates SAMA-compliant
  // tax-inclusive totals, so there is no UI affordance to flip it
  // off. The override forwarded to the backend always carries
  // `taxable: true`.
  const preview = useMemo(() => {
    return computeAmountPreview({
      currency,
      itemsOriginalSubtotal: loadPrice ?? 0,
      fees: {
        applied: fees.map(f => ({
          type: f.type,
          amount: f.amount || 0,
          calcType: 'add_to_subtotal' as const,
        })) as Fee[],
      },
      tax: {
        applied: [{ id: 'saudi_vat', percentage: taxRate, amount: 0 } as never],
      },
    });
  }, [currency, loadPrice, fees, taxRate]);

  const tax = preview.tax.total;
  const total = preview.total;

  const updateFee = (index: number, patch: Partial<FeeDraft>) => {
    setFees(prev => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };
  const removeFee = (index: number) => {
    setFees(prev => prev.filter((_, i) => i !== index));
  };
  const addFee = () => {
    const usedTypes = new Set(fees.map(f => f.type));
    const next =
      FEE_TYPES.find(ft => !usedTypes.has(ft) && !HIDDEN_FEE_TYPES.has(ft)) ?? 'other';
    setFees(prev => [...prev, { type: next, amount: 0 }]);
  };

  const feeOptions: SelectAutoOption[] = FEE_TYPES.filter(ft => !HIDDEN_FEE_TYPES.has(ft)).map(
    ft => ({
      value: ft,
      label: t(`c-price:fee_types.${ft}`, { defaultValue: ft }),
    }),
  );

  const handleSave = () => {
    // Treat an unset input as an explicit `0` so the user can save a
    // zero price (e.g. internal moves, free legs). The publish-readiness
    // gate and the per-leg banner distinguish "unresolved" from "real
    // zero" using `priceOverride` presence, not the numeric value.
    const effectiveLoadPrice = loadPrice ?? 0;
    const matchesAgreed =
      hasAgreedPrice &&
      agreedSubtotal !== undefined &&
      Math.abs(agreedSubtotal - effectiveLoadPrice) <= 0.001;
    if (hasAgreedPrice && (!useCustomPrice || matchesAgreed)) {
      setPriceOverride(destinationIndex, undefined);
      toast.success(
        t('drawer.price_edit.draft_cleared_toast', {
          defaultValue: 'Price reset to the calculated rate',
        }),
      );
      onClose();
      return;
    }
    const next: PublishOrderPriceOverride = {
      itemsOriginalSubtotal: effectiveLoadPrice,
      currency,
      // VAT is always applied — see the comment on `preview` above.
      taxable: true,
      fees: fees.length > 0 ? (fees.map(f => ({ type: f.type, amount: f.amount })) as Fee[]) : undefined,
    };
    setPriceOverride(destinationIndex, next);
    toast.success(
      t('drawer.price_edit.draft_saved_toast', { defaultValue: 'Price set for this destination' }),
    );
    onClose();
  };

  const handleClearOverride = () => {
    setUseCustomPrice(false);
    if (hasAgreedPrice && agreedSubtotal !== undefined) {
      setLoadPrice(agreedSubtotal);
      setFees([]);
    }
    setPriceOverride(destinationIndex, undefined);
    toast.success(
      t('drawer.price_edit.draft_cleared_toast', {
        defaultValue: 'Price reset to the calculated rate',
      }),
    );
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 bg-white w-[520px] sm:max-w-[520px]">
        <SheetHeader className="px-5 pt-5 pb-3 border-b">
          <SheetTitle>
            {t('drawer.price_edit.draft_title', {
              defaultValue: 'Edit price for destination {{n}}',
              n: destinationIndex + 1,
            })}
          </SheetTitle>
          <SheetDescription>
            {t('drawer.price_edit.draft_description', {
              defaultValue:
                'Sets a one-time price for this destination only. Other destinations keep their calculated price.',
            })}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {hasAgreedPrice && pricingSource === 'directed' && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2.5">
              <AlertCircleIcon className="size-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <div className="text-[12px] font-medium text-emerald-900">
                  {t('drawer.price_edit.agreement_price_badge', {
                    defaultValue: 'Agreement price',
                  })}
                </div>
                <div className="text-[11px] text-emerald-800">
                  {t('drawer.price_edit.draft_agreement_price_hint', {
                    defaultValue:
                      'A saved rate applies to this lane. Uncheck custom price below to keep it.',
                  })}
                </div>
              </div>
            </div>
          )}
          {hasAgreedPrice &&
            (pricingSource === 'seller_own' || pricingSource === 'buyer_own') && (
              <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50/70 px-3 py-2.5">
                <AlertCircleIcon className="size-4 text-sky-600 mt-0.5 shrink-0" />
                <div className="space-y-0.5">
                  <div className="text-[12px] font-medium text-sky-900">
                    {t('drawer.price_edit.everyone_price_badge', {
                      defaultValue: 'Public rate',
                    })}
                  </div>
                  <div className="text-[11px] text-sky-800">
                    {t('drawer.price_edit.draft_everyone_price_hint', {
                      defaultValue:
                        'A published rate applies to this lane. Uncheck custom price below to keep it.',
                    })}
                  </div>
                </div>
              </div>
            )}
          {!hasAgreedPrice && pricingSource === 'unresolved' && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50/70 px-3 py-2.5">
              <AlertCircleIcon className="size-4 text-rose-600 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <div className="text-[12px] font-medium text-rose-900">
                  {t('drawer.price_edit.unresolved_price_badge', {
                    defaultValue: 'Awaiting price',
                  })}
                </div>
                <div className="text-[11px] text-rose-800">
                  {t('drawer.price_edit.draft_unresolved_price_hint', {
                    defaultValue: 'No saved rate matched this lane. Enter a price to confirm.',
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <PriceInput
              label={t('drawer.price_edit.load_price_label', { defaultValue: 'Load price' })}
              value={loadPrice}
              onChange={handleLoadPriceChange}
              currency={currency}
              priceType="before_tax"
              hint={t('drawer.price_edit.before_tax_hint', { defaultValue: 'Before tax' })}
              disabled={hasAgreedPrice && !useCustomPrice}
            />

            {hasAgreedPrice && (
              <UseCustomPriceToggle
                useCustomPrice={useCustomPrice}
                onUseCustomPriceChange={handleUseCustomPriceChange}
                agreedSubtotal={agreedSubtotal}
                loadPrice={loadPrice}
                currency={currency}
              />
            )}
          </div>

          {/* Fees editor — same vocabulary as order-mode but stored
              locally on the override. The publish payload only forwards
              the load price + currency + taxable triple to the backend
              (`createOrderAPISchema.price`); fees attached here surface
              in the side-panel breakdown so the user can see what their
              total is, then re-attach them through the order drawer
              after the order is created. */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[12px] font-medium text-gray-700">
                {t('drawer.price_edit.fees_label', { defaultValue: 'Fees' })}
              </label>
              <button
                type="button"
                onClick={addFee}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 disabled:opacity-40 disabled:pointer-events-none">
                <PlusIcon className="size-3.5" />
                {t('drawer.price_edit.add_fee', { defaultValue: 'Add fee' })}
              </button>
            </div>

            {fees.length === 0 ? (
              <div className="mt-2 rounded-md border border-dashed border-gray-200 p-3 text-xs text-gray-500">
                {t('drawer.price_edit.no_fees', { defaultValue: 'No fees yet.' })}
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                {fees.map((fee, index) => (
                  <div
                    key={`${fee.type}-${index}`}
                    className="rounded-md border border-gray-200 bg-white p-2.5 space-y-2">
                    <SelectAuto
                      options={feeOptions}
                      value={fee.type}
                      onChange={value => updateFee(index, { type: value as FeeType })}
                      placeholder={t('drawer.price_edit.fees_label', { defaultValue: 'Fees' })}
                    />
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <PriceInput
                          value={fee.amount}
                          onChange={next => updateFee(index, { amount: next ?? 0 })}
                          currency={currency}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFee(index)}
                        aria-label={t('drawer.price_edit.fee_remove', {
                          defaultValue: 'Remove fee',
                        })}
                        className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                        <TrashIcon className="size-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-md border border-gray-200 bg-gray-50 overflow-hidden">
            <div className="px-3.5 py-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  {t('drawer.price_edit.total', { defaultValue: 'Total' })}
                </span>
                <Price amount={total} currency={currency} size="lg" className="font-semibold" />
              </div>
              <div className="flex items-center justify-between text-[11px] text-gray-500">
                <span>
                  {t('drawer.price_edit.total_with_tax_hint', {
                    defaultValue: 'Includes tax',
                  })}
                  <span className="mx-1">·</span>
                  {t('drawer.price_edit.tax_line', {
                    defaultValue: 'Tax ({{percent}}%)',
                    percent: Math.round(taxRate * 100),
                  })}
                </span>
                <Price amount={tax} currency={currency} size="xs" />
              </div>
            </div>

            <div className="border-t border-gray-200 bg-white px-3.5 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[12px] font-medium text-gray-700">
                  {t('drawer.price_edit.payment_method_label', {
                    defaultValue: 'Payment method',
                  })}
                </span>
                <div className="flex items-center gap-1 rounded-md bg-gray-100 p-0.5">
                  {PAYMENT_METHOD_OPTIONS.map(({ value, icon: Icon }) => {
                    const isActive = paymentMethod === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setPaymentMethod(value)}
                        aria-pressed={isActive}
                        className={cn(
                          'flex items-center gap-1.5 rounded px-2.5 py-1 text-[12px] font-medium transition-colors',
                          isActive
                            ? 'bg-white text-primary ring-1 ring-primary/40 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700',
                        )}>
                        <Icon
                          className={cn(
                            'size-3.5',
                            isActive ? 'text-primary' : 'text-gray-500',
                          )}
                        />
                        {t(`drawer.price_edit.payment_method.${value}`, { defaultValue: value })}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

        </div>

        <div className="border-t px-5 py-4 flex gap-3">
          {override || (hasAgreedPrice && useCustomPrice) ? (
            <Button variant="ghost" onClick={handleClearOverride}>
              {t('drawer.price_edit.draft_clear_cta', { defaultValue: 'Reset' })}
            </Button>
          ) : (
            <span className="flex-1" />
          )}
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            className="flex-1"
            onClick={handleSave}>
            {t('drawer.price_edit.save_cta', { defaultValue: 'Save price' })}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface RoutePromptRowProps {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  label: React.ReactNode;
}

/**
 * Compact "save / update Route" prompt. Sits inline under the load
 * price input, styled as a soft callout card so it reads as a
 * suggestion rather than a hard form field. Keeping the prompt next
 * to the price field (instead of in a footer) makes the cause/effect
 * obvious — the user changed the price, so this is what happens to
 * the saved price.
 */
function RoutePromptRow({ checked, onCheckedChange, label }: RoutePromptRowProps) {
  return (
    <label className="flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50/50 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors">
      <Checkbox
        checked={checked}
        onCheckedChange={value => onCheckedChange(!!value)}
        className="mt-0.5 shrink-0"
      />
      <span className="text-[11px] text-blue-900">{label}</span>
    </label>
  );
}
